import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { api, assetUrl } from '../lib/api';
import GuideBody from '../components/GuideBody';
import { MachineCard } from './Machines';
import CTASection from '../components/CTASection';
import useSEO from '../hooks/useSEO';

const GuideDetail = () => {
    const { slug } = useParams();
    const [g, setG] = useState(null);
    const [missing, setMissing] = useState(false);
    const [products, setProducts] = useState([]);

    useEffect(() => {
        setG(null); setMissing(false);
        api.get(`/guides/${slug}`).then((r) => setG(r.data)).catch(() => setMissing(true));
    }, [slug]);
    useEffect(() => {
        if (!g?.related_machine_ids?.length) { setProducts([]); return; }
        api.get('/machines').then((r) => setProducts(r.data.filter((m) => g.related_machine_ids.includes(m.id))
            .sort((a, b) => g.related_machine_ids.indexOf(a.id) - g.related_machine_ids.indexOf(b.id))));
    }, [g]);

    useSEO(g ? { title: `${g.title} — Dry Ice Bulls Buying Guide`, description: g.excerpt } : { title: 'Buying Guide — Dry Ice Bulls' });

    if (missing) {
        return (
            <div className="container-page pt-32 pb-24" data-testid="guide-missing">
                <h1 className="font-display font-black uppercase text-4xl text-neutral-900">Guide not found</h1>
                <Link to="/knowledge" className="mt-6 inline-flex items-center gap-2 label-caps text-brand-primary">All guides <ArrowRight className="h-4 w-4" /></Link>
            </div>
        );
    }
    if (!g) return <div className="container-page pt-32 pb-24 text-neutral-400 label-caps">Loading…</div>;

    return (
        <div className="pt-24" data-testid="guide-page">
            <article className="container-page max-w-3xl">
                <Link to="/knowledge" className="inline-flex items-center gap-2 label-caps text-xs text-neutral-500 hover:text-brand-primary" data-testid="guide-back"><ArrowLeft className="h-4 w-4" /> Knowledge Center</Link>
                <div className="mt-6 label-caps text-brand-primary">/ Buying guide</div>
                <h1 className="mt-3 font-display font-black uppercase tracking-tighter text-4xl md:text-6xl text-neutral-900 leading-[0.95]" data-testid="guide-title">{g.title}</h1>
                {g.excerpt && <p className="mt-5 text-lg text-neutral-600">{g.excerpt}</p>}
                {g.cover_image && <img src={assetUrl(g.cover_image)} alt={g.title} className="mt-8 w-full aspect-[16/9] object-cover border border-neutral-200" data-testid="guide-cover" />}
                <div className="mt-10"><GuideBody body={g.body} /></div>

                {g.related_links?.length > 0 && (
                    <div className="mt-12 flex flex-wrap gap-3" data-testid="guide-links">
                        {g.related_links.filter((l) => l.to).map((l, i) => (
                            <Link key={i} to={l.to} className={`inline-flex items-center gap-2 px-6 py-3.5 label-caps transition-colors ${i === 0 ? 'bg-brand-primary hover:bg-brand-primaryHover text-white font-bold' : 'border border-neutral-300 hover:border-neutral-900 text-neutral-900'}`} data-testid={`guide-link-${i}`}>
                                {l.label} <ArrowRight className="h-4 w-4" />
                            </Link>
                        ))}
                    </div>
                )}
            </article>

            {products.length > 0 && (
                <section className="container-page mt-20" data-testid="guide-products">
                    <div className="label-caps text-brand-primary mb-2">/ Our picks for this guide</div>
                    <h2 className="font-display font-black uppercase text-3xl text-neutral-900 mb-8">Shop the setup</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">{products.map((m) => <MachineCard key={m.id} m={m} />)}</div>
                </section>
            )}

            <CTASection variant="knowledge" heading="Questions about your setup?" subheading="Text or WhatsApp us anytime — we answer within 24 hours."
                context={{ trackingLocation: 'guide_bottom', subject: `Guide question: ${g.title}` }} testid="guide-cta-bottom" className="mt-16" />
        </div>
    );
};

export default GuideDetail;
